import {
    Kws,
    Suspension,
    chainOrSuspend,
    pyException,
    pyList,
    pyNone,
    pyObject,
    pyStr,
    toJs,
    tryCatchOrSuspend,
} from "@Sk";
import PyDefUtils from "PyDefUtils";
import {
    Component,
    ComponentConstructor,
    EventDescription,
    MarginPropertyValue,
    UnsetPropertyValue,
} from "@runtime/components/Component";
import { mkInvalidComponent } from "@runtime/runner/component-creation";
import { designerApi } from "@runtime/runner/component-designer-api";
import { getUnsetMargin } from "@runtime/runner/component-property-utils-api";
import { data } from "@runtime/runner/data";
import { ResolvedForm, getFormInstantiator } from "@runtime/runner/instantiation";
import { initNativeSubclass, pyTryFinally } from "@runtime/runner/py-util";
import { PropertyDescription } from "../shared-types";

type PropertiesByName = { [name: string]: PropertyDescription & { defaultValue?: any } };

type setter = (...args: any) => void | Suspension;

type PropertyDescriptionGetSet = PropertyDescription & {
    defaultValue?: any;
    deprecateFromRuntimeV3?: boolean;
    set?: setter;
    getUnset?: () => UnsetPropertyValue | undefined | null;
};

const getPropertiesByName = (properties: PropertyDescription[]) =>
    Object.fromEntries(properties.map((property) => [property.name, property]));

// TODO this doesn't really work incomplete (getters and setters depend on _anvil) and imports a hideous dependency on PyDefUtils;
// We should probably just port the property groups into a new world somehow (possibly via a common "this is how we implement native components in JS")
const propsFromGroups = (groupNames: string[], overrides: any = {}): PropertyDescriptionGetSet[] =>
    PyDefUtils.assembleGroupProperties(groupNames, overrides).map(
        ({
            name,
            type,
            group,
            description,
            important,
            hidden,
            options,
            iconsets,
            allowCustomValue,
            designerHint,
            multiline,
            accept,
            deprecated,
            deprecateFromRuntimeV3,
            allowBindingWriteback,
            priority,
            defaultValue,
            set,
        }) => ({
            name,
            type,
            group,
            description,
            important,
            hidden,
            options,
            iconsets,
            allowCustomValue,
            designerHint,
            multiline,
            accept,
            deprecated,
            deprecateFromRuntimeV3,
            supportsWriteback: allowBindingWriteback,
            priority,
            defaultValue,
            set,
        })
    );

function getsetsFromProperties(properties: PropertyDescriptionGetSet[]) {
    const getsets = [];
    for (const { name, set, defaultValue } of properties) {
        if (!set) {
            continue;
        }
        getsets.push([
            name,
            {
                $get() {
                    return this.$d.quick$lookup(new pyStr(name)) ?? defaultValue ?? pyNone;
                },
                $set(newValue: pyObject | undefined) {
                    // TODO undefined signals deletion
                    // TODO if this matches the old call signature then I think the call sig is (self, jqueryEl, value)
                    this.$d.mp$ass_subscript(new pyStr(name), newValue);
                    return set(this, this._anvil.element, toJs(newValue));
                },
            },
        ]);
    }
    return Object.fromEntries(getsets);
}

// Candidates for being abstracted into some kind of util
const setProperties = (self: DesignerRepeatingPanel, kws: Kws = [], propertyDescriptions: PropertiesByName) => {
    const seen = new Set();
    let objectToInvalidProperties = true;
    for (let i = 0; i < kws.length; i += 2) {
        if (kws[i] === "__ignore_property_exceptions") {
            objectToInvalidProperties = false;
            break;
        }
    }

    const attrs = [];

    for (let i = 0; i < kws.length; i += 2) {
        const name = kws[i] as string,
            value = kws[i + 1] as pyObject;
        if (name in propertyDescriptions) {
            attrs.push([new pyStr(name), value]);
            seen.add(name);
        } else if (objectToInvalidProperties) {
            throw new Sk.builtin.ValueError(`Component type "${self.tp$name}" has no property "${name}"`);
        }
    }
    for (const [name, value] of Object.entries(propertyDescriptions)) {
        if (!seen.has(name)) {
            attrs.push([new pyStr(name), value.defaultValue || pyNone]);
        }
    }
    return chainOrSuspend(
        undefined,
        ...attrs.map(
            ([k, v]) =>
                () =>
                    Sk.abstr.sattr(self, k, v, true)
        )
    );
};

const PROPERTIES: PropertyDescriptionGetSet[] = [
    {
        name: "items",
        type: "object",
        description: "A list of items for which the 'item_template' will be instantiated",
        important: false,
        group: null,
    },
    {
        name: "item_template",
        type: "form",
        description: "The name of the form to repeat for every item",
        important: true,
        group: null,
    },
    ...propsFromGroups(["appearance", "layout", "layout_margin", "tooltip", "user data"]),
];

const BY_NAME = getPropertiesByName(PROPERTIES);

const EVENTS: EventDescription[] = [
    { name: "show", description: "When the repeating panel is shown on the screen", parameters: [] },
    { name: "hide", description: "When the repeating panel is removed from the screen", parameters: [] },
];

interface DesignerRepeatingPanelConstructor extends ComponentConstructor {
    new (): DesignerRepeatingPanel;
}

interface DesignerRepeatingPanel extends Component {
    _RepeatingPanel: {
        element: HTMLDivElement;
        itemTemplate: null | pyObject;
        components: null | Component[];
        onPage: boolean;
    };
    _anvil: { element: JQuery; domNode: HTMLDivElement };
}

type InstantiationStack = { formSpec?: ResolvedForm; prev: InstantiationStack; seenFormIds: Set<string> } | undefined;

let currentlyInstantiating: InstantiationStack;

export const DesignRepeatingPanelV3: DesignerRepeatingPanelConstructor = Sk.abstr.buildNativeClass(
    "anvil.DesignRepeatingPanelv3",
    {
        constructor: function DesignRepeatingPanelV3() {},
        base: Component,
        proto: {
            anvil$hookSpec: {
                setupDom(this: DesignerRepeatingPanel) {
                    return this._RepeatingPanel.element;
                },
                getDomElement() {
                    return (this as unknown as DesignerRepeatingPanel)._RepeatingPanel.element;
                },
                getProperties() {
                    // It would be nice to calculate all this statically, but data.app isn't populated until after this module is loaded.
                    return PROPERTIES.filter(
                        ({ type }) =>
                            data.app.runtime_options.version >= 3 || !["margin", "padding", "spacing"].includes(type)
                    ).map(({ set, defaultValue, deprecateFromRuntimeV3, deprecated, ...prop }) => ({
                        ...prop,
                        deprecated: deprecated || (deprecateFromRuntimeV3 && data.app.runtime_options.version >= 3),
                    }));
                },
                getEvents() {
                    return EVENTS;
                },
                getInteractions(this: DesignerRepeatingPanel) {
                    const rp = this._RepeatingPanel;
                    return [
                        {
                            type: "whole_component",
                            title: "Edit item template form",
                            icon: "edit_form",
                            callbacks: {
                                execute() {
                                    rp.components && designerApi.startEditingSubform(rp.components[0]);
                                },
                            },
                            default: true,
                        },
                    ];
                },
                getUnsetPropertyValues() {
                    const currentMargin = toJs<MarginPropertyValue>(
                        this.$d.quick$lookup(new pyStr("margin")) ?? pyNone
                    );
                    return {
                        margin: getUnsetMargin(this._anvil.element[0], currentMargin),
                    };
                },
            },
        },
        flags: {
            sk$klass: true,
        },
        slots: {
            tp$new(args, kws) {
                // TODO should this be in thee tp$new?
                const self = Component.prototype.tp$new.call(this, args, kws) as DesignerRepeatingPanel;
                self.$d ??= new Sk.builtin.dict();
                const element = document.createElement("div");
                self._RepeatingPanel = {
                    element,
                    itemTemplate: null,
                    components: null,
                    onPage: false,
                };
                // Hack to make the setters work
                self._anvil = { element: $(self._RepeatingPanel.element), domNode: self._RepeatingPanel.element };
                self._RepeatingPanel.element.classList.add("anvil-design-repeating-panel");

                let timeoutId: number | undefined;
                const preFetchTimeout = 100;

                const handleMouseEnter = (): void => {
                    clearTimeout(timeoutId);
                    timeoutId = window.setTimeout(() => {
                        const subForm = self._RepeatingPanel.components?.[0];
                        if (!subForm) return;
                        designerApi.preEditSubform(subForm);
                    }, preFetchTimeout);
                };

                const handleMouseLeave = (): void => {
                    clearTimeout(timeoutId);
                };

                // This is a bit hacky - it relies event-interceptor allowing mouseenter/mouseleave to pass through
                // but really design components shouldn't expect any mouse events to bubble
                // TODO - support this publicly
                element.addEventListener("mouseenter", handleMouseEnter);
                element.addEventListener("mouseleave", handleMouseLeave);

                return self;
            },
            tp$init(args, kws) {
                this.tp$setattr(new pyStr("items"), pyNone);
                return setProperties(this, kws, BY_NAME);
            },
        },
        getsets: {
            item_template: {
                $get() {
                    return this._RepeatingPanel.itemTemplate;
                },
                $set(newItemTemplate) {
                    const rp = this._RepeatingPanel;
                    if (newItemTemplate === rp.itemTemplate) {
                        return;
                    }
                    this._RepeatingPanel.itemTemplate = newItemTemplate;
                    // Clear the element and create three copies
                    if (newItemTemplate !== pyNone) {
                        rp.element.innerText = "";
                        rp.components = null;
                        return tryCatchOrSuspend(
                            () =>
                                chainOrSuspend(
                                    getFormInstantiator(
                                        { requestingComponent: this },
                                        newItemTemplate as pyStr | ComponentConstructor,
                                        { preferLiveDesign: false }
                                    ),
                                    (instantiate) => {
                                        // Are we looping?
                                        const thisFormSpec = instantiate.anvil$instantiatorForForm;
                                        let seenBefore = false;
                                        if (thisFormSpec) {
                                            let ci = currentlyInstantiating;
                                            while (ci) {
                                                if (
                                                    ci.formSpec &&
                                                    ci.formSpec.formName === thisFormSpec.formName &&
                                                    ci.formSpec.depId === thisFormSpec.depId
                                                ) {
                                                    rp.element.append(
                                                        mkInvalidComponent(
                                                            `${thisFormSpec.formName} nested inside itself`
                                                        ).anvil$hooks.domElement!
                                                    );
                                                    return;
                                                }
                                                ci = ci.prev;
                                            }

                                            seenBefore =
                                                currentlyInstantiating?.seenFormIds.has(
                                                    thisFormSpec.qualifiedClassName
                                                ) ?? false;
                                            currentlyInstantiating?.seenFormIds.add(thisFormSpec.qualifiedClassName);
                                        }

                                        const create = () =>
                                            chainOrSuspend(
                                                instantiate([], !rp.components ? "item_template" : undefined),
                                                (pyComponent: Component) => {
                                                    (rp.components ||= []).push(pyComponent);
                                                    return chainOrSuspend(
                                                        pyComponent.anvil$hooks.setupDom(),
                                                        (domElement) => {
                                                            rp.element.append(domElement);
                                                            return pyComponent.anvilComponent$setParent(this, {
                                                                onRemove() {},
                                                            });
                                                        }
                                                    );
                                                }
                                            );
                                        currentlyInstantiating = {
                                            formSpec: thisFormSpec,
                                            prev: currentlyInstantiating,
                                            seenFormIds: currentlyInstantiating?.seenFormIds ?? new Set(),
                                        };
                                        return pyTryFinally(
                                            () => (seenBefore ? create() : chainOrSuspend(create(), create, create)),
                                            () => {
                                                currentlyInstantiating = currentlyInstantiating?.prev;
                                            }
                                        );
                                    }
                                ),
                            (e: pyException) => {
                                rp.element.append(
                                    mkInvalidComponent(`Item template error: ${e.toString()}`).anvil$hooks.domElement!
                                );
                            }
                        );
                    } else {
                        rp.element.innerHTML = "<center><i>item_template not set</i></center>";
                    }
                },
            },
            __dict__: Sk.generic.getSetDict,
            ...getsetsFromProperties(PROPERTIES),
        },
        methods: {
            get_components: {
                $meth() {
                    return new pyList(this._RepeatingPanel.components || []);
                },
                $flags: { NoArgs: true },
            },
        },
    }
);

initNativeSubclass(DesignRepeatingPanelV3);
